import {
  ConnectVerifierModel,
  CopyEpdModel,
  CreateEPDModel,
  EPDClient,
  FileParameter,
  GeographicalScopesClient,
  MultiCopyEpdModel,
} from './EpdClient';
import { EPD_DICTIONARY_CODES } from './api/constants';

const client = new EPDClient(process.env.REACT_APP_API_URL);
const geographicalClient = new GeographicalScopesClient(process.env.REACT_APP_API_URL);
// To prevent race conditions when confirming update without moving focus from updated title.
// we keep track of the current promise and await it before starting a new one.
// Fix for the EpdCopyGroupWizard.
let currentPromise: Promise<any> | null = null;

class EPDService {
  static getEpds = async (accountId: string, companyId?: string, smartFilter?: string, excludeCollectionId?: string) => {
    return await client.getAll(smartFilter, accountId, companyId, excludeCollectionId);
  };

  static getEpd = async (epdId: string) => {
    return await client.get(epdId);
  };

  static createEpd = async (accountId: string) => {
    const createEpd: CreateEPDModel = { accountId: accountId };
    const result = await client.create(createEpd);
    return result;
  };

  static createCompilerEpd = async (accountId: string) => {
    const createEpd: CreateEPDModel = { accountId: accountId };
    const result = await client.createCompilerEpd(createEpd);
    return result;
  };

  static copyEpd = async (
    epdId: string,
    accountId: string,
    newEpdName: string,
    copyProperties: any[],
    targetIsCompilerFormat?: boolean
  ) => {
    const createEpd: CopyEpdModel = {
      epdId: epdId,
      accountId: accountId,
      newEpdName,
      copyProperties,
      targetIsCompilerFormat,
    };
    const result = await client.createCopy(createEpd);
    return result;
  };

  static copyMultiEpd = async (model: MultiCopyEpdModel) => {
    const result = await client.createMultiCopy(model);
    return result;
  };

  static updateProperty = async (epdId: string, propertyName: string, newValue: any) => {
    while (currentPromise) {
      try {
        await currentPromise;
      } catch {
        // Ignore errors from previous promise
      }
    }
    currentPromise = client.updateEPDProperty({ epdId, propertyName, newValue });
    try {
      const result = await currentPromise;
      return result;
    } finally {
      currentPromise = null;
    }
  };

  static confirmUpdate = async (epdId: string, versionId: string) => {
    while (currentPromise) {
      try {
        await currentPromise;
      } catch {
        // Ignore errors from previous promise
      }
    }
    currentPromise = client.confirmUpdate(epdId, versionId);
    try {
      const result = await currentPromise;
      return result;
    } finally {
      currentPromise = null;
    }
  };

  static removeEpdFile = async (epdFileId: string, epdVersionId: string) => {
    const result = await client.removeEPDFile(epdFileId, epdVersionId);
    return result;
  };

  static addEpdFile = async (
    epdId: string,
    propertyName: string,
    fileName: string,
    data: any,
    documentTypeId: string | null,
    documentLanguageId: string | null
  ) => {
    const addFile: FileParameter = { fileName, data };
    const result = await client.addEPDFile(epdId, propertyName, addFile, documentTypeId, documentLanguageId);

    return result;
  };

  static addLCAFile = async (epdId: string, file: File) => await client.addLCAFile(epdId, file);

  static arrangeEpdFiles = async (epdFileIds: string[]) => {
    const result = await client.arrangeEpdFiles(epdFileIds);
    return result;
  };

  static getLocations = async () => {
    const result = await geographicalClient.getAll(null);
    return result;
  };

  static connectDeveloper = async (epdId: string, membershipId: string) => {
    const result = await client.connectDeveloper(epdId, membershipId);
    return result;
  };

  static connectVerifier = async (model: ConnectVerifierModel) => {
    const result = await client.connectVerifier(model);
    return result;
  };

  static getEpdGroups = async (accountId: string, companyId?: string, smartFilter?: string) => {
    return await client.getAllGroups(smartFilter, accountId, companyId);
  };

  static getCopyGroupEpds = async (accountId: string, companyId?: string) => {
    const groups = await client.getAllGroups(undefined, accountId, companyId);
    const groupsWithItems = groups.filter((group) => group.itemsCount > 0);

    if (groupsWithItems.length === 0) {
      return [];
    }

    const results = await Promise.all(
      groupsWithItems.map(async (group) => {
        const fullGroup = await client.getEpdGroup(group.id);

        return (fullGroup.epds ?? []).map((epd) => ({
          copyGroupId: group.id,
          epd,
          versionDate: fullGroup.versionDate,
          validityDate: fullGroup.validityDate,
        }));
      })
    );

    return results.flat();
  };

  static validateLCAFile = async (fileName: string, data: any) => {
    const file: FileParameter = { fileName, data };
    const result = await client.validateLCAFile(file);
    return result;
  };

  static getConformityStandards = async () => {
    const result = await client.getDictionaryValues(EPD_DICTIONARY_CODES.CONFORMITY_STANDARDS);
    return result;
  };

  static getBiogenicCarbonContentUnits = async () => {
    const result = await client.getDictionaryValues(EPD_DICTIONARY_CODES.BIOGENIC_CARBON_CONTENT_UNITS);
    return result;
  };

  static getOkobaudatProductCategories = async () => {
    const result = await client.getOkobaudatProductCategories();
    return result;
  };
}

export default EPDService;
